import { runInAction } from "mobx";
import { NavigateFunction } from "react-router-dom";
import { Result } from "../helper/result";
import { UiBaseError } from "../model/ui_base_error";
import { message } from "antd";
import { ClassConstructor, plainToInstance } from "class-transformer";
import { CrudHttpRepository, HttpError } from "../repository/http_repository";
import { ValidationModel } from "../model/validation_model";

export type CoreError = HttpError | Error;

export interface Drawer {
  name: string;
  status: boolean;
}

interface IMessage {
  successMessage?: string;
  errorMessage?: string;
}
export abstract class UiLoader {
  isLoading = false;
  async httpHelper<T>(callBack: Promise<Result<any, T>>) {
    this.isLoading = true;

    let result: Result<any, T>;
    try {
      result = await callBack;
    } catch (error) {
      result = Result.error(error);
    }
    if (result.isFailure()) {
      runInAction(() => {
        this.isLoading = false;
        this.errorHandingStrategy(result.error);
      });
      return result.forward();
    }

    runInAction(() => { this.isLoading = false; });
    return result;
  }
  abstract errorHandingStrategy: (error?: any) => void;

  mapOk = async <T>(property: string, callBack: Promise<Result<CoreError, T>>) => {
    return (await this.httpHelper(callBack)).map((el) => {
      // @ts-ignore
      this[property] = el;
    });
  };
  messageHttp = async <T>(callBack: Promise<Result<CoreError, T>>, report?: IMessage) => {
    return (await this.httpHelper(callBack)).fold(
      (_s) => {
        if (report && report.successMessage) message.success(report.successMessage);
      },
      (_e) => {
        if (report && report.errorMessage) message.error(report.errorMessage);
      }
    );
  };
}
export class SimpleErrorState extends UiLoader {
  errorHandingStrategy = () => {
    this.isError = true;
  };
  isError = false;
}
export class ModalStore extends SimpleErrorState {
  isModalOpen: boolean = false;
  modalShow = () => {
    this.isModalOpen = true;
  };

  modalClickOk = () => {
    this.isModalOpen = false;
  };

  modalCancel = () => {
    this.isModalOpen = false;
  };
}
export abstract class UiErrorState<T> extends UiLoader {
  errorHandingStrategy = (error: T) => {
    console.log(error);
  };
  abstract init(navigate?: NavigateFunction): Promise<any>;
  dispose() { }
  errors: UiBaseError[] = [];
}

export abstract class DrawerState<E> extends UiErrorState<E> {
  titleDrawer: string = "";
  drawers: Drawer[];
  constructor(drawerEnum: Object) {
    super();
    this.drawers = Object.entries(drawerEnum).map((k, v) => {
      return {
        name: k.at(1) ?? "",
        status: false,
      };
    });
  }
  editDrawer(drawerName: string, status: boolean): void {
    this.titleDrawer = drawerName;
    this.drawers = this.drawers.map((el) => {
      if (el.name === drawerName) {
        el.status = status;
      }
      return el;
    });
  }
}
export abstract class UiDrawerFormState<V, E> extends DrawerState<E> {
  abstract viewModel: V;
  updateForm(value: Partial<V>) {
    //@ts-ignore
    this.viewModel = Object.assign(this.viewModel, value);
  }
  loadDependency = (viewModel: V) => {
    this.viewModel = viewModel;
  };
  loadClassInstance = (instance: ClassConstructor<V>, viewModel: V) => {
    this.viewModel = plainToInstance(instance, viewModel);
  };
}
export abstract class FormState<V, E> extends UiErrorState<E> {
  abstract viewModel: V;

  updateFormCallback = () => {
  }
  updateForm(value: Partial<V>) {
    //@ts-ignore
    this.viewModel = Object.assign(this.viewModel, value);
    this.updateFormCallback()
  }
  loadDependency = (viewModel: V | undefined) => {
    if (viewModel) this.viewModel = viewModel;
  };
  loadClassInstance = (instance: ClassConstructor<V>, viewModel: V) => {
    this.viewModel = plainToInstance(instance, viewModel);
  };
  isModalOpen: boolean = false;
  modalShow = () => {
    this.isModalOpen = true;
  };

  modalClickOk = () => {
    this.isModalOpen = false;
  };

  modalCancel = () => {
    this.isModalOpen = false;
  };
}

export abstract class CrudFormStore<V extends ValidationModel, E, R extends CrudHttpRepository<V>> extends FormState<V, E> {
  page: number = 0;
  models?: V[];
  abstract repository: R;
  errorMessage = "";
  errorHandingStrategy = (error: any) => {
    this.errorMessage = error instanceof Error ? error.message : String(error);
  };
  private mutate = async (request: () => Promise<Result<any, any>>, closeModal = false) => {
    if (this.isLoading) return Result.error("An operation is already in progress");
    this.errorMessage = "";
    const result = await this.httpHelper(request());
    if (result.isSuccess()) {
      if (closeModal) this.modalClickOk();
      await this.refresh();
    }
    return result;
  };
  create = (model: V) => this.mutate(() => this.repository.addModel(model), true);
  delete = (id: string) => this.mutate(() => this.repository.deleteModel(id));
  update = (model: V) => this.mutate(() => this.repository.edit(model), true);
  read = () => this.repository.getPage(this.page);
  refresh = async () => {
    this.errorMessage = "";
    const result = await this.httpHelper(this.read());
    if (result.isSuccess()) {
      runInAction(() => { this.models = result.getOrThrow(); });
      if (this.models?.length === 0 && this.page > 0) {
        runInAction(() => { this.page -= 1; });
        await this.refresh();
      }
    }
  };
  nextPage = async () => {
    if (this.isLoading) return;
    this.page += 1;
    await this.refresh();
  };
  prevPage = async () => {
    if (this.isLoading || this.page === 0) return;
    this.page -= 1;
    await this.refresh();
  };
  async initCrud() {
    await this.refresh();
  }
}
